/*  MUI  */
import { Box, Button, FormControlLabel, Menu, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { AuvikSwitch } from 'application/Shared/Components/Switches';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { useManagedTableV2Context } from '../ManagedTableV2ContextProvider';

export const ManagedTableSelectColumnsV2 = ({ anchorEl, handleClose }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentTableView, table } = useManagedTableV2Context();

    const columns = table.getAllColumns();
    const alphabetizedColumns = [...columns];
    alphabetizedColumns.sort((a, b) => {
        const nameA = t(a.columnDef.name);
        const nameB = t(b.columnDef.name);
        return nameA.localeCompare(nameB);
    });

    // Extract columnFilters of the table state to reset, if necessary
    const { columnFilters } = table.getState();

    return (
        <Menu
            anchorEl={anchorEl}
            id="showHideColumnsMenu"
            onClose={() => {
                handleClose();
            }}
            open={Boolean(anchorEl)}
            slotProps={{
                paper: {
                    'data-testid': 'showHideColumnsMenuPaper',
                    'data-trackid': 'showHideColumnsMenuPaper',
                    sx: {
                        boxShadow: theme.shadows[24],
                        minWidth: '300px',
                        padding: '16px'
                    }
                }
            }}
            MenuListProps={{ style: { padding: 0 } }}
            variant="menu"
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb="16px"
            >
                <Typography
                    variant="p"
                    sx={{ fontSize: FontSizes[2], fontWeight: 600 }}
                >
                    {t('Table.customizeColumns')}
                </Typography>
                <Button
                    data-testid="resetToDefaultColumnVisibility"
                    onClick={() => {
                        // Reset the column filters, visibility, order, and sort to the initialState.
                        table.setColumnVisibility(
                            currentTableView?.state?.columnVisibility
                        );
                        table.setColumnOrder(
                            currentTableView?.state?.columnOrder
                        );
                        table.setSorting(currentTableView?.state?.sorting);
                        table.setColumnFilters(
                            currentTableView?.state?.columnFilters
                        );
                    }}
                    sx={{ padding: 0 }}
                    variant="text"
                >
                    <Typography style={{ textTransform: 'capitalize' }}>
                        {t('Common.reset')}
                    </Typography>
                </Button>
            </Box>

            {alphabetizedColumns.map(
                (column) =>
                    column.getCanHide() &&
                    column.columnDef.visible !== false && (
                        <Box key={column.id} sx={{ paddingY: '4px' }}>
                            <FormControlLabel
                                aria-label={`show or hide ${t(
                                    column.columnDef.name
                                )} column`}
                                control={
                                    <AuvikSwitch
                                        checked={column.getIsVisible()}
                                        onClick={() => {
                                            if (column.getIsVisible()) {
                                                // If we are hiding a column, we need to clear its own filters.
                                                const filteredFilters =
                                                    columnFilters?.filter(
                                                        (filter) =>
                                                            filter.id !==
                                                            column.id
                                                    );
                                                table.setColumnFilters([
                                                    ...filteredFilters
                                                ]);
                                            }
                                            column.toggleVisibility();
                                        }}
                                        inputProps={{
                                            'aria-label': 'controlled'
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="p"
                                        style={{ marginLeft: '8px' }}
                                    >
                                        {t(column.columnDef.name)}
                                    </Typography>
                                }
                                style={{ marginLeft: 0 }}
                            />
                        </Box>
                    )
            )}
        </Menu>
    );
};
